/* Productgallery van ELÛNA.
 *
 * HERSTELD op 19 augustus 2026. Deze versie is de gallery-code van voor de Dawn 16-update van
 * 18 augustus, die het bestand terugzette naar de standaard van Shopify en daarmee vier dingen
 * sloopte: video's speelden niet meer af, de teller bleef op 1 staan, de pijlknoppen deden niets,
 * en `is-active` verschoof niet meer bij het swipen (waardoor een videoslide onzichtbaar bleef).
 *
 * Waarom die extra logica nodig is, gemeten op 19-08-2026: de slides zijn breder dan de slider
 * (offset 385px tegen 375px), waardoor Dawn's eigen `slidesPerPage` op 0 uitkomt. Daarmee valt
 * de standaardnavigatie van Dawn stil en vuurt `slideChanged` niet meer. Deze klasse zet daarom
 * zelf de actieve slide, de teller en het afspelen.
 *
 * Eén inhoudelijke wijziging tegenover de oude versie: video's starten GEDEMPT (keuze Kevin).
 */
if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // Wire prev/next buttons to setActiveMedia so they work on all layouts
        this.elements.viewer.querySelectorAll('.slider-buttons .slider-button').forEach((btn) => {
          btn.addEventListener('click', this.onNavButtonClick.bind(this));
        });

        // Take ownership of the counter total — prevent SliderComponent from overwriting it
        this.houdKnoppenKlikbaar();

        const actualCount = this.elements.viewer.querySelectorAll('[id^="Slide-"]').length;
        const totalEl = this.elements.viewer.querySelector('.slider-counter--total');
        if (totalEl) totalEl.textContent = actualCount;
        // Null out SliderComponent's reference so its update() never touches the total
        setTimeout(() => {
          if (this.elements.viewer.pageTotalElement !== undefined) {
            this.elements.viewer.pageTotalElement = null;
          }
          if (this.elements.viewer.totalPages !== undefined) {
            this.elements.viewer.totalPages = actualCount;
          }
          if (totalEl) totalEl.textContent = actualCount;
        }, 0);

        // Autoplay video if the initially active slide contains one
        const initialActive = this.elements.viewer.querySelector('[id^="Slide-"].is-active');
        if (initialActive) this.playActiveMedia(initialActive);

        // Preload all deferred-media (videos) so they're ready when user navigates to them
        // Done after a short delay so it doesn't block initial page rendering
        setTimeout(() => this.preloadDeferredMedia(), 1500);

        // Always sync counter when SliderComponent fires slideChanged (covers mobile scroll)
        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));

        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onNavButtonClick(event) {
        // Deze handler doet de navigatie op ELKE breedte, en loopt rond: na de laatste slide
        // kom je weer bij de eerste, en voor de eerste bij de laatste.
        //
        // Hij stond eerder uit zodra SliderComponent actief was (sliderItemOffset > 0), in de
        // veronderstelling dat Dawn het op mobiel zelf zou doen. Gemeten 19-08-2026 op 390px
        // breed: dat doet Dawn NIET. Zijn onButtonClick liet scrollLeft op 0 staan, en de oorzaak
        // is zijn eigen rekensom `slidesPerPage = floor((clientWidth - offsetLeft) / itemOffset)`.
        // Sinds de slides de volle breedte hebben (om de strook van de volgende foto weg te
        // halen) is die som (390 - 0) / 400 = 0,975 en dus 0, waardoor zijn scrollstap
        // 0 * 400 = 0 pixels werd. De pijlen waren op mobiel dus zichtbaar maar dood.
        // Vandaar preventDefault en stopPropagation: wij navigeren, Dawn niet.
        const viewer = this.elements.viewer;
        event.preventDefault();
        event.stopPropagation();

        const slides = Array.from(viewer.querySelectorAll('[id^="Slide-"]'));
        if (slides.length < 2) return;
        const activeIndex = slides.findIndex((s) => s.classList.contains('is-active'));
        const current = activeIndex === -1 ? 0 : activeIndex;
        const isPrev = event.currentTarget.name === 'previous';
        const next = isPrev
          ? (current - 1 + slides.length) % slides.length
          : (current + 1) % slides.length;
        this.setActiveMedia(slides[next].dataset.mediaId, false);
      }

      houdKnoppenKlikbaar() {
        // De gallery loopt rond, dus geen enkele pijl hoort ooit uitgeschakeld te zijn.
        // Dawn zet toch `disabled` op de vorige-knop zodra de lijst op scrollpositie 0 staat,
        // en op de volgende-knop aan het eind. Een disabled button vuurt GEEN click-event,
        // hoeveel pointer-events en opacity je er in CSS ook op zet. Gemeten 19-08-2026:
        // daardoor deed de linkerpijl op de eerste slide niets, terwijl hij er wel klikbaar
        // uitzag. Deze observer haalt het attribuut er direct weer af. Dat is geen lus: na het
        // verwijderen vuurt de observer nog een keer, ziet niets meer staan, en stopt.
        const knoppen = this.elements.viewer.querySelectorAll('.slider-buttons .slider-button');
        if (!knoppen.length) return;
        const ontgrendel = () => {
          knoppen.forEach((b) => {
            if (b.hasAttribute('disabled')) b.removeAttribute('disabled');
          });
        };
        ontgrendel();
        new MutationObserver(ontgrendel).observe(this.elements.viewer, {
          subtree: true,
          attributes: true,
          attributeFilter: ['disabled'],
        });
      }

      scrollNaarSlide(target) {
        // Horizontaal naar een slide scrollen, en dit MOET zo.
        // Gemeten 19-08-2026 op 390px breed, met de lijst op `scroll-snap-type: x mandatory`
        // en `scroll-behavior: smooth`:
        //   scrollTo({left: 1600, behavior: 'smooth'})  ->  scrollLeft bleef 0
        //   target.scrollIntoView({inline: 'start'})    ->  scrollLeft bleef 0
        //   scrollLeft = 1600 met snap en behavior uit  ->  scrollLeft werd 1600 (correct)
        // De mandatory snap trekt een programmatische smooth-scroll dus terug naar de eerste
        // snap-positie. Daardoor liep de teller wel door naar 5 terwijl je nog naar slide 1 keek.
        // We zetten snap en smooth daarom kort uit, scrollen, en halen die inline waarden er in
        // de volgende frame weer AF (removeProperty, niet een bewaarde waarde terugzetten: dat
        // gaf bij twee klikken achter elkaar 'none' als bewaarde waarde en dan blijft snap uit).
        const lijst = target.parentElement;
        if (!lijst || lijst.scrollWidth <= lijst.clientWidth) return;
        lijst.style.scrollSnapType = 'none';
        lijst.style.scrollBehavior = 'auto';
        lijst.scrollLeft = target.offsetLeft;
        requestAnimationFrame(() => {
          lijst.style.removeProperty('scroll-snap-type');
          lijst.style.removeProperty('scroll-behavior');
        });
      }

      onSlideChanged(event) {
        const allSlides = Array.from(this.elements.viewer.querySelectorAll('[id^="Slide-"]'));
        const currentSlide = event.detail.currentElement;
        const slideIndex = allSlides.indexOf(currentSlide);

        // Sync is-active class so CSS rules (deferred-media visibility) apply
        allSlides.forEach((s) => s.classList.remove('is-active'));
        if (currentSlide) currentSlide.classList.add('is-active');

        // Sync counter — also correct total in case SliderComponent overwrote it
        const currentEl = this.elements.viewer.querySelector('.slider-counter--current');
        if (currentEl) currentEl.textContent = slideIndex + 1;
        const totalEl = this.elements.viewer.querySelector('.slider-counter--total');
        if (totalEl) totalEl.textContent = allSlides.length;

        // Also fix totalPages on the SliderComponent so it stays correct
        if (this.elements.viewer.totalPages !== undefined) {
          this.elements.viewer.totalPages = allSlides.length;
        }

        // Sync dots/numbers
        this.elements.viewer.querySelectorAll('.slider-counter__link').forEach((link, i) => {
          link.classList.toggle('slider-counter__link--active', i === slideIndex);
        });

        // Autoplay video if the current slide has one
        if (currentSlide) this.playActiveMedia(currentSlide);

        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentSlide.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const allSlides = Array.from(this.elements.viewer.querySelectorAll('[id^="Slide-"]'));
        const activeMedia =
          allSlides.find((s) => s.dataset.mediaId === mediaId) ||
          allSlides[0];
        if (!activeMedia) return;

        const prevIndex = allSlides.findIndex((s) => s.classList.contains('is-active'));
        const nextIndex = allSlides.indexOf(activeMedia);
        const goingForward = prevIndex === -1 || nextIndex > prevIndex;

        activeMedia.style.setProperty('--slide-direction', goingForward ? '4rem' : '-4rem');

        allSlides.forEach((element) => element.classList.remove('is-active'));
        activeMedia.classList.add('is-active');

        // Update counter display
        const slideIndex = allSlides.indexOf(activeMedia);
        const currentEl = this.elements.viewer.querySelector('.slider-counter--current');
        if (currentEl) currentEl.textContent = slideIndex + 1;

        // Update dots/numbers active state
        this.elements.viewer.querySelectorAll('.slider-counter__link').forEach((link, i) => {
          link.classList.toggle('slider-counter__link--active', i === slideIndex);
        });

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            this.scrollNaarSlide(activeMedia);
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        // Only pause videos within this gallery — not all videos on the page
        this.querySelectorAll('video').forEach((v) => v.pause());
        this.querySelectorAll('.js-youtube').forEach((v) => {
          v.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        });
        this.querySelectorAll('.js-vimeo').forEach((v) => {
          v.contentWindow.postMessage('{"method":"pause"}', '*');
        });
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (!deferredMedia) return;

        const existingVideo = deferredMedia.querySelector('video');
        if (existingVideo) {
          // Video already loaded (from preload) — play directly without calling loadContent,
          // which avoids pauseAllMedia() and eliminates the stutter
          // GEDEMPT, keuze van Kevin (19-08-2026): een video in de gallery start nooit met
          // geluid. Dit was hier eerder `muted = false` met een terugval naar gedempt; die
          // terugval sloeg in de praktijk altijd aan, want elke browser blokkeert autoplay
          // met geluid. Nu staat het er expliciet, zodat het geen toeval meer is.
          existingVideo.muted = true;
          existingVideo.setAttribute('muted', 'muted');
          existingVideo.play().catch(() => {});
        } else {
          // First load — need loadContent to extract the <template>
          deferredMedia.loadContent(false);
          // loadContent calls pauseAllMedia — re-activate external section videos
          this.reactivateExternalVideos();
          setTimeout(() => {
            const video = deferredMedia.querySelector('video');
            if (video) {
              // gedempt, zie de toelichting hierboven
              video.muted = true;
              video.setAttribute('muted', 'muted');
              video.play().catch(() => {});
            }
          }, 100);
        }
      }

      preloadDeferredMedia() {
        // Load all deferred-media templates into the DOM so videos start buffering
        // but keep them paused — playActiveMedia will handle play on navigation
        this.elements.viewer.querySelectorAll('.deferred-media:not([loaded])').forEach((dm) => {
          // Het autoplay-attribuut moet UIT DE TEMPLATE, dus voordat we hem uitpakken.
          // loadContent() voegt de video toe EN roept daarna zelf window.pauseAllMedia() aan.
          // Staat autoplay er dan nog in, dan is de browser al aan het starten en breekt die
          // pause de play af: "AbortError: The play() request was interrupted by a call to
          // pause()". Gemeten 19-08-2026 met een controlegroep: 2 van die fouten per bezoek op
          // deze versie tegen 0 op het thema zonder deze preload.
          // playActiveMedia start de video zelf zodra zijn slide actief wordt, dus het attribuut
          // is hier niet nodig. Het blijft wel in de liquid staan als terugval voor het geval
          // deze JS niet laadt.
          const tpl = dm.querySelector('template');
          if (tpl && tpl.content) {
            tpl.content.querySelectorAll('video').forEach((tv) => tv.removeAttribute('autoplay'));
          }
          dm.loadContent(false);
          // Immediately pause any autoplay video so only the active slide plays
          const video = dm.querySelector('video');
          if (video) {
            // Het autoplay-attribuut moet EERST weg. loadContent() zet de video met dat attribuut
            // in de DOM, de browser begint dan te spelen, en de pause() hieronder breekt dat af.
            // Dat levert per paginabezoek de fout "AbortError: The play() request was interrupted
            // by a call to pause()" op; gemeten 19-08-2026, 2 keer per bezoek in de console.
            // playActiveMedia start de video zelf zodra zijn slide actief is, dus het attribuut
            // is hier niet nodig.
            video.removeAttribute('autoplay');
            video.autoplay = false;
            video.muted = true;
            if (!video.paused) video.pause();
          }
        });
        // loadContent() calls window.pauseAllMedia() — re-activate external section videos
        this.reactivateExternalVideos();
      }

      reactivateExternalVideos() {
        // Re-play videos outside this gallery that were paused by window.pauseAllMedia()
        // (e.g. SS Product Videos section). Uses a short delay so it runs after pauseAllMedia finishes.
        setTimeout(() => {
          document.querySelectorAll('video[autoplay], video[loop]').forEach((v) => {
            // Skip videos inside this gallery
            if (this.contains(v)) return;
            v.muted = true;
            v.play().catch(() => {});
          });
        }, 200);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
